import { Dialog, FormDatePicker } from "@/components";
import { Badge, Button, Input, Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui";
import { cn } from "@/lib/utils";
import { useRemovePropertyAssociationMutation, useUpdatePropertyAssociationMutation } from "@/services";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQueryClient } from "@tanstack/react-query";
import { Lock, Plus, Star } from "lucide-react";
import moment from "moment";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { toast } from "@/components/Toast";
import AddTenantDialog from "./AddTenantDialog";
import { z } from "zod";

const TENANT = "TENANT";
const OWNER = "OWNER";
const fmtDate = (v?: string) => (v ? moment(v).format("MM/DD/YYYY") : "—");
const startOfDay = (v?: string) => (v ? moment(v).format("YYYY-MM-DDT00:00:00") : null);
const endOfDay = (v?: string) => (v ? moment(v).format("YYYY-MM-DDT23:59:59") : null);
const humanize = (v?: string) => (v ? v.replace(/_/g, " ") : "—");
const errorMessage = (error?: any) =>
  typeof error?.response?.data?.message === "string" ? error.response.data.message : "Something went wrong";

const tenantSchema = z
  .object({ startDate: z.string().min(1, "Please select Start Date."), endDate: z.string().optional().nullable() })
  .refine((d) => !d.endDate || !moment(d.endDate).isBefore(moment(d.startDate), "day"),
    { message: "End Date cannot be before Start Date.", path: ["endDate"] });

interface OwnersTenantsSectionProps {
  account: any;            // GET /property-ownerships/{id}/account (people, ownership type)
  communityId: string;
  canEdit: boolean;
  onOpenOwner: (userId: number) => void;
}

/** Owners and tenants on the current title. Owners are read-only (OCP / conversion); tenants can be added and maintained. */
const OwnersTenantsSection = ({ account, communityId, canEdit, onOpenOwner }: OwnersTenantsSectionProps) => {
  const queryClient = useQueryClient();
  const [openAdd, setOpenAdd] = useState(false);
  const [editing, setEditing] = useState<any>(null);   // tenant row being edited
  const [endReason, setEndReason] = useState("");
  const people: any[] = account?.people || [];

  const refresh = () => {
    queryClient.invalidateQueries({ queryKey: ["ownership-account", communityId, String(account?.ownershipId)] });
    queryClient.invalidateQueries({ queryKey: ["ownership-grid"] });
  };

  const { mutate: updateAssociation, isPending: isUpdating } = useUpdatePropertyAssociationMutation({
    onError: (e) => toast.error(errorMessage(e)),
    onSuccess: () => {
      toast.success("Tenant updated Successfully");
      setEditing(null);
      refresh();
    },
  });
  const { mutate: removeAssociation, isPending: isRemoving } = useRemovePropertyAssociationMutation({
    onError: (e) => toast.error(errorMessage(e)),
    onSuccess: () => {
      toast.success("Tenancy ended Successfully");
      setEditing(null);
      refresh();
    },
  });

  const tenantForm = useForm<any>({ resolver: zodResolver(tenantSchema) });
  const openEdit = (p: any) => {
    tenantForm.reset({ startDate: p.startDate || "", endDate: p.endDate || "" });
    setEndReason("");
    setEditing(p);
  };
  const onSaveTenant = (v: any) => {
    // PATCH ignores nulls, so an existing End Date can't be cleared here — use End Tenancy to close it
    updateAssociation({
      id: editing.propertyUserAssociationId,
      data: { startDate: startOfDay(v.startDate), ...(v.endDate ? { endDate: endOfDay(v.endDate) } : {}) },
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-sm p-4 md:p-6 flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold">Owners and tenants</h3>
          <p className="text-sm text-gray-500">
            Owner rows are read-only and controlled by conversion/OCP. Tenants can be added and maintained here;
            occupancy is derived from the active owners and tenants on this title.
          </p>
        </div>
        {canEdit && account?.active ? (
          <Button type="button" onClick={() => setOpenAdd(true)}><Plus className="w-4 h-4" /> Add Tenant Manually</Button>
        ) : <></>}
      </div>

      <div className="border rounded-lg overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              {["Line", "Owner / User ID", "Person", "Relationship", "Title Share", "Effective Period", "Status", "Maintenance"]
                .map((h) => <TableHead key={h} className="font-semibold text-primary whitespace-nowrap">{h}</TableHead>)}
            </TableRow>
          </TableHeader>
          <TableBody>
            {people.length === 0 ? (
              <TableRow><TableCell colSpan={8} className="text-center text-gray-500 h-16">No people linked to this title.</TableCell></TableRow>
            ) : people.map((p, i) => {
              const isOwner = p.associationType === OWNER;
              const isTenant = p.associationType === TENANT;
              return (
                <TableRow key={p.propertyUserAssociationId}>
                  <TableCell>{String(i + 1).padStart(2, "0")}</TableCell>
                  <TableCell>
                    <button type="button" className="text-primary font-semibold hover:underline" onClick={() => onOpenOwner(p.userId)}>{p.userId}</button>
                    <p className="text-xs text-gray-500">Association {p.propertyUserAssociationId}</p>
                  </TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1 font-medium">
                      {[p.firstName, p.lastName].filter(Boolean).join(" ") || p.userName || "—"}
                      {p.primaryUser ? <Star className="w-4 h-4 fill-yellow-400 text-yellow-400" aria-label="Primary" /> : <></>}
                    </span>
                    <p className="text-xs text-gray-500">{[p.email, p.phoneNumber].filter(Boolean).join(" · ")}</p>
                  </TableCell>
                  <TableCell>
                    <span className="flex items-center gap-1">
                      <Badge variant="outline" className={cn("rounded-md px-2 py-0.5 text-xs font-semibold",
                        isTenant ? "border-purple-200 bg-purple-50 text-purple-700" : "border-blue-200 bg-blue-50 text-primary")}>
                        {humanize(p.associationType)}
                      </Badge>
                      {isOwner ? <Lock className="w-3.5 h-3.5 text-gray-400" aria-label="Read-only" /> : <></>}
                    </span>
                  </TableCell>
                  <TableCell>{isOwner && p.sharePercentage != null ? `${Number(p.sharePercentage)}%` : "Not applicable"}</TableCell>
                  <TableCell className="whitespace-nowrap">
                    {fmtDate(p.startDate)}
                    <p className="text-xs text-gray-500">{p.endDate ? `to ${fmtDate(p.endDate)}` : "to Present"}</p>
                  </TableCell>
                  <TableCell>
                    <Badge variant="outline" className={cn(p.active ? "border-green-200 bg-green-50 text-green-700" : "border-gray-200 bg-gray-100 text-gray-600", "rounded-md px-2 py-0.5")}>
                      {p.active ? "Active" : "Inactive"}
                    </Badge>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    {isOwner ? (
                      <span className="text-xs text-gray-500 flex items-center gap-1"><Lock className="w-3 h-3" /> OCP only</span>
                    ) : isTenant && canEdit && p.active ? (
                      <button type="button" className="text-primary underline text-sm" onClick={() => openEdit(p)}>Edit tenant</button>
                    ) : <span className="text-xs text-gray-400">—</span>}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>

      {openAdd ? (
        <AddTenantDialog open={openAdd} setOpen={setOpenAdd} account={account} communityId={communityId} onAdded={refresh} />
      ) : <></>}

      {editing ? (
        <Dialog
          open={!!editing}
          setOpen={(o: boolean) => !o && setEditing(null)}
          title={`Edit tenant — ${[editing.firstName, editing.lastName].filter(Boolean).join(" ")}`}
          onSave={tenantForm.handleSubmit(onSaveTenant)}
          isLoading={isUpdating}
          contentClassName="sm:max-w-[520px]"
        >
          <FormProvider {...tenantForm}>
            <div className="flex flex-col gap-4">
              <div className="grid grid-cols-2 gap-4">
                <FormDatePicker name="startDate" control={tenantForm.control} label="Start Date" isRequired
                  error={(tenantForm.formState.errors as any).startDate?.message} />
                <FormDatePicker name="endDate" control={tenantForm.control} label="End Date"
                  error={(tenantForm.formState.errors as any).endDate?.message} />
              </div>
              <div className="border-t pt-4 flex flex-col gap-2">
                <p className="text-sm font-medium">End tenancy</p>
                <p className="text-xs text-gray-500">Closes this tenancy now and marks it inactive. Occupancy updates automatically.</p>
                <Input placeholder="Reason (optional)" value={endReason} onChange={(e) => setEndReason(e.target.value)} />
                <Button type="button" variant="outline" className="self-start border-red-300 text-red-600 hover:bg-red-50"
                  disabled={isRemoving}
                  onClick={() => removeAssociation({ id: editing.propertyUserAssociationId, reason: endReason.trim() || undefined })}>
                  End Tenancy
                </Button>
              </div>
            </div>
          </FormProvider>
        </Dialog>
      ) : <></>}
    </div>
  );
};

export default OwnersTenantsSection;
